/**
 * Website Configuration Service (Public Booking App)
 * Reads homepage settings from Firestore - READ ONLY
 */

import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';

export interface HeroConfig {
  headline: string;
  subtext: string;
  imageUrl: string;
  ctaText: string;
  ctaLink: string;
}

export interface ShopInfo {
  name: string;
  address: string;
  phone: string;
  hours: string;
  description: string;
}

export interface SocialLinks {
  instagram: string;
  facebook: string;
  twitter: string;
}

export interface Testimonial {
  id: string;
  name: string;
  rating: number;
  text: string;
  date: string;
}

export interface SiteStats {
  happyClients: string;
  haircutsDone: string;
  yearsExperience: string;
  rating: string;
  reviewCount: string;
}

export interface WebsiteConfig {
  logo?: string;          // shop logo (data-URI or URL); falls back to the bundled SLIK mark
  tagline?: string;       // shown under the shop name everywhere; default "Grooming for Every Man"
  shopInfo: ShopInfo;
  hero: HeroConfig;
  featuredServiceIds: string[];
  featuredBarberIds: string[];
  testimonials: Testimonial[];
  galleryImages: string[];
  socialLinks: SocialLinks;
  stats: SiteStats;
}

// Default configuration (fallback)
const DEFAULT_CONFIG: WebsiteConfig = {
  shopInfo: {
    name: 'SLIK Barber Lounge',
    address: '123 Main Street, Downtown',
    phone: '(555) 123-4567',
    hours: 'Mon-Sat: 9AM - 8PM',
    description: 'Premium barbershop experience with skilled barbers, modern atmosphere, and exceptional service.',
  },
  hero: {
    headline: 'Premium Cuts. Exceptional Style.',
    subtext: 'Experience the best in grooming with our team of skilled professionals.',
    imageUrl: 'https://images.unsplash.com/photo-1585747860715-2ba37e788b70?w=1920&q=80',
    ctaText: 'Book Now',
    ctaLink: '/book',
  },
  featuredServiceIds: [],
  featuredBarberIds: [],
  testimonials: [
    {
      id: '1',
      name: 'Marcus J.',
      rating: 5,
      text: 'Best barbershop in town! The attention to detail is incredible.',
      date: '2 weeks ago',
    },
    {
      id: '2',
      name: 'David R.',
      rating: 5,
      text: 'Finally found my go-to spot. Clean cuts every time.',
      date: '1 month ago',
    },
    {
      id: '3',
      name: 'Chris T.',
      rating: 5,
      text: 'Professional service and great atmosphere. Highly recommend!',
      date: '3 weeks ago',
    },
  ],
  galleryImages: [
    'https://images.unsplash.com/photo-1503951914875-452162b0f3f1?w=600&q=80',
    'https://images.unsplash.com/photo-1621605815971-fbc98d665033?w=600&q=80',
    'https://images.unsplash.com/photo-1622286342621-4bd786c2447c?w=600&q=80',
  ],
  socialLinks: {
    instagram: '',
    facebook: '',
    twitter: '',
  },
  stats: {
    happyClients: '500+',
    haircutsDone: '10K+',
    yearsExperience: '5+',
    rating: '4.9',
    reviewCount: '120+',
  },
};

const CONFIG_DOC_ID = 'main';

/**
 * Get website configuration from Firestore
 */
export async function getWebsiteConfig(): Promise<WebsiteConfig> {
  try {
    const configRef = doc(db, 'website_config', CONFIG_DOC_ID);
    const docSnap = await getDoc(configRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        ...DEFAULT_CONFIG,
        ...data,
      } as WebsiteConfig;
    }

    return DEFAULT_CONFIG;
  } catch (error) {
    console.error('Error fetching website config:', error);
    return DEFAULT_CONFIG;
  }
}

/**
 * Subscribe to real-time config updates
 */
export function subscribeToWebsiteConfig(
  callback: (config: WebsiteConfig) => void
): () => void {
  const configRef = doc(db, 'website_config', CONFIG_DOC_ID);

  const unsubscribe = onSnapshot(configRef, (docSnap) => {
    if (docSnap.exists()) {
      const data = docSnap.data();
      callback({
        ...DEFAULT_CONFIG,
        ...data,
      } as WebsiteConfig);
    } else {
      callback(DEFAULT_CONFIG);
    }
  }, (error) => {
    console.error('Error subscribing to website config:', error);
    callback(DEFAULT_CONFIG);
  });

  return unsubscribe;
}

export { DEFAULT_CONFIG };
